
import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Bot, Link, X } from 'lucide-react';
import { useArticleAI } from '@/contexts/article-ai/ArticleAIContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

interface ArticleAskQuestionProps {
  article: any;
}

const ArticleAskQuestion: React.FC<ArticleAskQuestionProps> = ({ article }) => {
  const { askQuestion, conversation, isAskLoading } = useArticleAI();
  const [questionText, setQuestionText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom when conversation updates
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (questionText.trim() && !isAskLoading) {
      askQuestion(questionText);
      setQuestionText('');
    }
  };

  return (
    <Card className="w-full border-primary/20 flex flex-col" style={{ height: '400px' }}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Bot className="h-5 w-5 mr-2 text-primary" />
            <CardTitle className="text-lg">Ask About This Article</CardTitle>
          </div>
        </div>
        <CardDescription>
          Ask questions about the article content
        </CardDescription>
      </CardHeader>
      <ScrollArea className="flex-1 px-4 py-2">
        {conversation.length === 0 ? (
          <div className="text-center p-6 text-muted-foreground">
            <p>Ask any question about this article and I'll help answer it based on the content.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {conversation.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`rounded-lg px-3 py-2 max-w-[80%] ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    {message.role === 'user' ? (
                      <User className="h-3 w-3 mr-1" />
                    ) : (
                      <Bot className="h-3 w-3 mr-1" />
                    )}
                    <span className="text-xs font-medium">
                      {message.role === 'user' ? 'You' : 'AI Assistant'}
                    </span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  
                  {message.role === 'assistant' && message.relevantSections && message.relevantSections.length > 0 && (
                    <div className="mt-2 text-xs">
                      <div className="font-semibold mb-1">Relevant sections:</div>
                      <ul className="space-y-1">
                        {message.relevantSections.map((section, idx) => (
                          <li key={idx} className="flex items-center">
                            <Link className="h-3 w-3 mr-1" />
                            <a 
                              href={`#section-${section.id}`} 
                              className="underline"
                            >
                              {section.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isAskLoading && (
              <div className="flex justify-start">
                <div className="rounded-lg px-3 py-2 bg-muted">
                  <div className="flex items-center">
                    <Bot className="h-3 w-3 mr-1" />
                    <span className="text-xs font-medium">AI Assistant</span>
                  </div>
                  <div className="flex items-center space-x-1 mt-1">
                    <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>
      <Separator />
      <CardFooter className="pt-2">
        <form onSubmit={handleSubmit} className="w-full flex gap-2">
          <Input
            placeholder="Ask a question about this article..."
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            disabled={isAskLoading}
            className="flex-1"
          />
          <Button type="submit" size="sm" disabled={isAskLoading || !questionText.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
};

export default ArticleAskQuestion;
